import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Users,
  Building2,
  UserPlus,
  Link,
  Unlink,
  CheckCircle,
  XCircle,
  Plus,
  Trash2,
  Edit,
  Eye,
  Search,
  RefreshCw,
  AlertCircle,
  LayoutDashboard,
  FolderOpen,
  UserCheck,
  User,
  Calendar,
  DollarSign,
  CreditCard,
  TrendingUp,
  BarChart3,
  Package,
  Tag,
  Ruler,
  Truck,
  ClipboardList,
  FileText,
  Shield,
  Settings,
  ChevronDown,
  ChevronRight,
  MapPin,
  Clock,
  Loader2,
  Save,
  X,
  UserCog,
  Link2,
  Home,
  Briefcase,
} from 'lucide-react';
import { toast } from 'sonner';
import pb from '../lib/pocketbaseClient';
import { cn } from '../lib/utils';

const accessLevels = [
  { value: 'full', label: 'Full Access', color: 'bg-green-100 text-green-700' },
  { value: 'readonly', label: 'Read Only', color: 'bg-blue-100 text-blue-700' },
  { value: 'limited', label: 'Limited', color: 'bg-yellow-100 text-yellow-700' },
];

export default function PartnerSiteManagement() {
  const [partners, setPartners] = useState([]);
  const [sites, setSites] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [siteEmployees, setSiteEmployees] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('assignments');
  const [search, setSearch] = useState('');
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [expandedPartner, setExpandedPartner] = useState(null);

  // --- Modal States ----------------------------------------------
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showPartnerDetail, setShowPartnerDetail] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  const [formData, setFormData] = useState({
    partner: '',
    site: '',
    accessLevel: 'full',
    notes: '',
    isActive: true,
  });

  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    fetchData();
    return () => { isMounted.current = false; };
  }, []);

  const fetchData = async () => {
    try {
      const [
        partnersRes,
        sitesRes,
        assignmentsRes,
        employeesRes,
        usersRes,
      ] = await Promise.all([
        pb.collection('partners').getFullList({ sort: 'name' }),
        pb.collection('accounts_projects').getFullList({ sort: 'name' }),
        pb.collection('partner_site_assignments').getFullList({
          expand: 'partner,site,assignedBy',
          sort: '-created',
        }),
        pb.collection('site_employees').getFullList({
          expand: 'site,user,assignedBy',
        }),
        pb.collection('users').getFullList({
          filter: 'role ~ "partner-" || role ~ "client" || role = "admin" || role = "accounts"',
        }),
      ]);

      if (isMounted.current) {
        setPartners(partnersRes);
        setSites(sitesRes);
        setAssignments(assignmentsRes);
        setSiteEmployees(employeesRes);
        setUsers(usersRes);
      }
    } catch (err) {
      console.error('Fetch error:', err);
      toast.error('Failed to load data');
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  // --- Helper Functions -------------------------------------------
  const getPartnerName = (id) => {
    const partner = partners.find(p => p.id === id);
    return partner?.name || 'Unknown Partner';
  };

  const getSiteName = (id) => {
    const site = sites.find(s => s.id === id);
    return site?.name || 'Unknown Site';
  };

  const getUserName = (id) => {
    const user = users.find(u => u.id === id);
    return user?.name || user?.email || 'Unknown User';
  };

  const getAccessBadge = (level) => {
    const found = accessLevels.find(a => a.value === level);
    return found ? (
      <Badge className={found.color}>{found.label}</Badge>
    ) : (
      <Badge variant="outline">{level}</Badge>
    );
  };

  // ✅ Get Partner Type - includes 'client'
  const getPartnerType = (partner) => {
    const user = users.find(u => u.id === partner.createdBy || u.email === partner.email);
    const role = user?.role || partner.role || 'client';
    
    if (role === 'partner-construction') {
      return <Badge className="bg-orange-100 text-orange-700 border-orange-200">Construction</Badge>;
    }
    if (role === 'partner-interior') {
      return <Badge className="bg-purple-100 text-purple-700 border-purple-200">Interior</Badge>;
    }
    if (role === 'client') {
      return <Badge className="bg-blue-100 text-blue-700 border-blue-200">Client</Badge>;
    }
    return <Badge className="bg-slate-100 text-slate-700 border-slate-200">Unknown</Badge>;
  };

  const getPartnerStatus = (partner) => {
    const user = users.find(u => u.id === partner.createdBy || u.email === partner.email);
    if (user?.verified) {
      return <Badge className="bg-green-100 text-green-700 border-green-200">Verified</Badge>;
    }
    return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">Pending</Badge>;
  };

  const getPartnerSites = (partnerId) => {
    return assignments.filter(a => a.partner === partnerId && a.isActive);
  };

  // --- Assignment Handlers ----------------------------------------
  const handleAssignSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');

    try {
      const data = {
        partner: formData.partner,
        site: formData.site,
        accessLevel: formData.accessLevel,
        notes: formData.notes || null,
        isActive: formData.isActive,
        assignedBy: pb.authStore.model?.id,
        assignedAt: new Date().toISOString(),
      };

      if (editingId) {
        await pb.collection('partner_site_assignments').update(editingId, data);
        toast.success('Assignment updated successfully');
      } else {
        await pb.collection('partner_site_assignments').create(data);
        toast.success('Site assigned to partner successfully');
      }
      handleCloseAssignModal();
      fetchData();
    } catch (err) {
      console.error(err);
      setFormError(err.message || 'Failed to save assignment');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteAssignment = async (id) => {
    if (!window.confirm('Remove this site access from partner?')) return;
    try {
      await pb.collection('partner_site_assignments').delete(id);
      toast.success('Site access removed');
      fetchData();
    } catch (err) {
      toast.error('Failed to remove: ' + err.message);
    }
  };

  const handleToggleAssignment = async (id, currentStatus) => {
    try {
      await pb.collection('partner_site_assignments').update(id, {
        isActive: !currentStatus,
      });
      toast.success(`Assignment ${!currentStatus ? 'activated' : 'deactivated'}`);
      fetchData();
    } catch (err) {
      toast.error('Failed to update: ' + err.message);
    }
  };

  const handleCloseAssignModal = () => {
    setShowAssignModal(false);
    setEditingId(null);
    setFormData({ partner: '', site: '', accessLevel: 'full', notes: '', isActive: true });
    setFormError('');
    setFormLoading(false);
  };

  // --- Filter Data -------------------------------------------------
  const filteredAssignments = assignments.filter(a => {
    const partnerName = getPartnerName(a.partner).toLowerCase();
    const siteName = getSiteName(a.site).toLowerCase();
    return partnerName.includes(search.toLowerCase()) || siteName.includes(search.toLowerCase());
  });

  const filteredPartners = partners.filter(p =>
    p.name?.toLowerCase().includes(search.toLowerCase()) ||
    p.email?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-10 h-10 animate-spin text-amber-500" />
          <p className="text-slate-500 font-medium">Loading partner data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* --- Header -------------------------------------------------- */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Shield className="w-6 h-6 text-amber-500" />
            Partner & Client Management
          </h1>
          <p className="text-sm text-slate-500">Assign sites to partners and clients</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            className="bg-amber-500 hover:bg-amber-600"
            onClick={() => setShowAssignModal(true)}
          >
            <Link className="w-4 h-4 mr-2" /> Assign Site
          </Button>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* --- Stats --------------------------------------------------- */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 font-medium">Total Partners</p>
            <p className="text-2xl font-bold text-slate-800">{partners.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 font-medium">Site Assignments</p>
            <p className="text-2xl font-bold text-blue-600">
              {assignments.filter(a => a.isActive).length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 font-medium">Site Employees</p>
            <p className="text-2xl font-bold text-green-600">
              {siteEmployees.filter(e => e.isActive).length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 font-medium">Sites</p>
            <p className="text-2xl font-bold text-slate-800">{sites.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* --- Main Tabs ---------------------------------------------- */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full max-w-2xl grid-cols-3">
          <TabsTrigger value="assignments">Site Assignments</TabsTrigger>
          <TabsTrigger value="partners">Partners & Clients</TabsTrigger>
          <TabsTrigger value="employees">Site Employees</TabsTrigger>
        </TabsList>

        {/* --- Search Bar -------------------------------------------- */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search partners, clients, sites..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button variant="outline" size="sm" onClick={() => setSearch('')}>
            Clear
          </Button>
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>

        {/* --- Site Assignments Tab ---------------------------------- */}
        <TabsContent value="assignments" className="mt-4">
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Partner/Client</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Site</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Access Level</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Status</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredAssignments.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-4 py-8 text-center text-slate-500">
                        No assignments found.
                      </td>
                    </tr>
                  ) : (
                    filteredAssignments.map(a => (
                      <tr key={a.id} className="hover:bg-slate-50/50 transition">
                        <td className="px-4 py-3 font-medium">{getPartnerName(a.partner)}</td>
                        <td className="px-4 py-3">{getSiteName(a.site)}</td>
                        <td className="px-4 py-3">{getAccessBadge(a.accessLevel)}</td>
                        <td className="px-4 py-3">
                          {a.isActive ? (
                            <Badge className="bg-green-100 text-green-700">Active</Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-700">Inactive</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="p-1.5 h-auto text-blue-600 hover:bg-blue-50"
                              onClick={() => handleToggleAssignment(a.id, a.isActive)}
                              title={a.isActive ? 'Deactivate' : 'Activate'}
                            >
                              {a.isActive ? (
                                <XCircle size={16} />
                              ) : (
                                <CheckCircle size={16} />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="p-1.5 h-auto text-red-600 hover:bg-red-50"
                              onClick={() => handleDeleteAssignment(a.id)}
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* --- Partners & Clients Tab ------------------------------ */}
        <TabsContent value="partners" className="mt-4">
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Name</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Contact</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Type</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Status</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredPartners.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-4 py-8 text-center text-slate-500">
                        No partners or clients found.
                      </td>
                    </tr>
                  ) : (
                    filteredPartners.map(p => {
                      const partnerSites = getPartnerSites(p.id);
                      const isExpanded = expandedPartner === p.id;

                      return (
                        <React.Fragment key={p.id}>
                          <tr
                            className="hover:bg-slate-50/50 transition cursor-pointer"
                            onClick={() => setExpandedPartner(isExpanded ? null : p.id)}
                          >
                            <td className="px-4 py-3 font-medium flex items-center gap-2">
                              {isExpanded ? (
                                <ChevronDown className="w-4 h-4 text-slate-400" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-slate-400" />
                              )}
                              {p.name}
                              <Badge variant="outline" className="text-xs">
                                {partnerSites.length} sites
                              </Badge>
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-sm">{p.phone || '-'}</div>
                              <div className="text-xs text-slate-400">{p.email || '-'}</div>
                            </td>
                            <td className="px-4 py-3">
                              {getPartnerType(p)}
                            </td>
                            <td className="px-4 py-3">
                              {getPartnerStatus(p)}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="p-1.5 h-auto text-amber-600 hover:bg-amber-50"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedPartner(p);
                                    setShowPartnerDetail(true);
                                  }}
                                  title="View Details"
                                >
                                  <Eye size={16} />
                                </Button>
                              </div>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan="5" className="px-4 py-3 bg-slate-50/30">
                                <div>
                                  <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2 flex items-center gap-1">
                                    <Building2 className="w-3 h-3" /> Assigned Sites
                                  </h4>
                                  {partnerSites.length === 0 ? (
                                    <p className="text-xs text-slate-400">No sites assigned</p>
                                  ) : (
                                    <div className="space-y-1">
                                      {partnerSites.map(a => (
                                        <div key={a.id} className="flex items-center justify-between bg-white rounded px-3 py-1.5 text-sm border">
                                          <span className="font-medium">{getSiteName(a.site)}</span>
                                          <div className="flex items-center gap-2">
                                            {getAccessBadge(a.accessLevel)}
                                            <Badge variant="outline" className="text-xs">
                                              {a.isActive ? 'Active' : 'Inactive'}
                                            </Badge>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* --- Site Employees Tab ------------------------------------ */}
        <TabsContent value="employees" className="mt-4">
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Site</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Employee</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Role</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Status</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {siteEmployees.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-4 py-8 text-center text-slate-500">
                        No employees assigned.
                      </td>
                    </tr>
                  ) : (
                    siteEmployees.map(e => (
                      <tr key={e.id} className="hover:bg-slate-50/50 transition">
                        <td className="px-4 py-3 font-medium">{getSiteName(e.site)}</td>
                        <td className="px-4 py-3">{getUserName(e.user)}</td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className="bg-indigo-50 text-indigo-700">
                            {e.role?.replace(/_/g, ' ')}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          {e.isActive ? (
                            <Badge className="bg-green-100 text-green-700">Active</Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-700">Inactive</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-1.5 h-auto text-red-600 hover:bg-red-50"
                            onClick={() => handleDeleteAssignment(e.id)}
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* --- Assign Site Modal -------------------------------------- */}
      {showAssignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-800">
                {editingId ? 'Edit Assignment' : 'Assign Site'}
              </h2>
              <button onClick={handleCloseAssignModal} className="p-1 hover:bg-slate-100 rounded">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            {formError && (
              <div className="bg-red-50 text-red-700 px-4 py-2 rounded-lg text-sm mb-4 border border-red-200">
                {formError}
              </div>
            )}

            <form onSubmit={handleAssignSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Partner/Client *</label>
                <select
                  value={formData.partner}
                  onChange={(e) => setFormData({ ...formData, partner: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-amber-400"
                  required
                >
                  <option value="">Select Partner/Client</option>
                  {partners.map(p => (
                    <option key={p.id} value={p.id}>{p.name} ({p.role || 'client'})</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Site *</label>
                <select
                  value={formData.site}
                  onChange={(e) => setFormData({ ...formData, site: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-amber-400"
                  required
                >
                  <option value="">Select Site</option>
                  {sites.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Access Level</label>
                <select
                  value={formData.accessLevel}
                  onChange={(e) => setFormData({ ...formData, accessLevel: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-amber-400"
                >
                  {accessLevels.map(a => (
                    <option key={a.value} value={a.value}>{a.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Optional notes"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="w-4 h-4 rounded border-slate-300 focus:ring-amber-400"
                />
                <label className="text-sm text-slate-700">Active</label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={handleCloseAssignModal}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-amber-500 hover:bg-amber-600" disabled={formLoading}>
                  {formLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {editingId ? 'Update' : 'Assign'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* --- Partner Detail Modal ---------------------------------- */}
      {showPartnerDetail && selectedPartner && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                <User className="w-6 h-6 text-amber-500" />
                {selectedPartner.name}
              </h2>
              <button onClick={() => setShowPartnerDetail(false)} className="p-1 hover:bg-slate-100 rounded">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-xs text-slate-500">Email</p>
                  <p className="text-sm font-medium">{selectedPartner.email || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Phone</p>
                  <p className="text-sm font-medium">{selectedPartner.phone || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Role</p>
                  {getPartnerType(selectedPartner)}
                </div>
                <div>
                  <p className="text-xs text-slate-500">Status</p>
                  {getPartnerStatus(selectedPartner)}
                </div>
              </div>

              {/* Assigned Sites */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1">
                  <Building2 className="w-4 h-4" /> Assigned Sites
                </h4>
                {getPartnerSites(selectedPartner.id).length === 0 ? (
                  <p className="text-sm text-slate-400">No sites assigned</p>
                ) : (
                  <div className="space-y-2">
                    {getPartnerSites(selectedPartner.id).map(a => (
                      <div key={a.id} className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-2">
                        <span className="font-medium">{getSiteName(a.site)}</span>
                        <div className="flex items-center gap-2">
                          {getAccessBadge(a.accessLevel)}
                          <Badge variant="outline" className="text-xs">
                            {a.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Quick Actions */}
              <div className="flex gap-2 pt-4 border-t">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setShowPartnerDetail(false);
                    setShowAssignModal(true);
                    setFormData({ ...formData, partner: selectedPartner.id });
                  }}
                >
                  <Link className="w-4 h-4 mr-1" /> Assign Site
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
